import { useRef, useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { useFieldStep } from "@/components/wizard/use-field-step";

import { type Draft, FIELDS } from "./fields";
import { InstagramFormSchemas, type InstagramFormInput, type InstagramFormValues } from "./form";
import { maskFor } from "./masks";
import { InstagramPayload } from "./payload";
import { fieldError } from "./validation";

type Phase = "form" | "send" | "done";

function formDraft({ hp: _hp, ...draft }: InstagramFormInput): Draft {
	return draft;
}

export function useInstagramWizard() {
	const [phase, setPhase] = useState<Phase>("form");
	const methods = useForm<InstagramFormInput, unknown, InstagramFormValues>({
		resolver: arktypeResolver(InstagramFormSchemas.form),
		mode: "onChange",
		defaultValues: { hp: "" },
	});
	const { clearErrors, control, getValues, handleSubmit, setError, setValue } = methods;
	useWatch({ control });
	const values = getValues();
	const draft = formDraft(values);
	const fieldStep = useFieldStep({
		active: phase === "form",
		fields: FIELDS,
		draft,
		setField: (key, value) => {
			setValue(key, value ?? "", { shouldDirty: true, shouldValidate: true });
		},
		maskFor,
		fieldError: (field) => fieldError(field, getValues(field.key) ?? ""),
		validateField: (field) => {
			const error = fieldError(field, getValues(field.key) ?? "");

			if (error !== null) {
				setError(field.key, { message: error });

				return false;
			}

			clearErrors(field.key);

			return true;
		},
		onComplete: () => setPhase("send"),
	});
	const { mutateAsync, isPending } = useMutation(orpc.leadInstagram.submit.mutationOptions());
	const submitting = useRef(false);
	const submit = handleSubmit(async (values) => {
		if (submitting.current) {
			return;
		}

		submitting.current = true;

		await mutateAsync(InstagramPayload.build({ draft: values, hp: values.hp }))
			.then(() => setPhase("done"))
			.catch(() => {
				submitting.current = false;
				toast.error("Não foi possível enviar o contato.");
			});
	});

	const goToForm = () => {
		setPhase("form");
	};

	return {
		phase,
		...fieldStep.state,
		draft,
		hp: values.hp,
		pending: isPending,
		actions: {
			...fieldStep.actions,
			submit,
			goToForm,
			setHp: (value: string) => setValue("hp", value, { shouldDirty: true }),
		},
	};
}

export type InstagramWizard = ReturnType<typeof useInstagramWizard>;
